import React, { useEffect, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import modals from 'constants/modals'
import { useModal } from 'utils/providers/ModalProvider'

import { ContentWrapper, ProfileAttribute } from 'shared'
import BeautyPreferencesForm from './BeautyPreferencesForm'
import SizingPreferencesForm from './SizingPreferencesForm'
import ProductInterestsForm from './ProductInterestsForm'
import FormProgressBar from 'components/FormProgressBar'

import { updateProfile as updateProfileApi } from 'api/user'

const useStyles = makeStyles(() => ({
  title: {
    fontSize: '32px',
    fontWeight: 700,
    lineHeight: 1,
  },
  progressContainer: {
    maxWidth: 240,
    margin: '20px 10px 40px 10px',
  },
}))

export interface PostponedFormStepProps {
  onStepChange: (newStep: number) => void
  step: PostponedFormStep
  updateProfile: (newAttribute: ProfileAttribute[]) => void
}

interface PostponedFormStep {
  id: number
  name: string
  Component: React.FC<PostponedFormStepProps>
}

const STEPS: PostponedFormStep[] = [
  {
    id: 0,
    name: 'Product Interests',
    Component: ({
      onStepChange,
      step,
      updateProfile,
    }: PostponedFormStepProps) => (
      <ProductInterestsForm
        onStepChange={onStepChange}
        step={step}
        updateProfile={updateProfile}
      />
    ),
  },
  {
    id: 1,
    name: 'Beauty preferences',
    Component: ({
      onStepChange,
      step,
      updateProfile,
    }: PostponedFormStepProps) => (
      <BeautyPreferencesForm
        onStepChange={onStepChange}
        step={step}
        updateProfile={updateProfile}
      />
    ),
  },
  {
    id: 2,
    name: 'Apparel sizing preferences',
    Component: ({
      onStepChange,
      step,
      updateProfile,
    }: PostponedFormStepProps) => (
      <SizingPreferencesForm
        onStepChange={onStepChange}
        step={step}
        updateProfile={updateProfile}
      />
    ),
  },
]

export const MAX_POSTPONED_CONTENT_WIDTH = 560

const PostponedProfileForm = () => {
  const classes = useStyles()
  const { showModal } = useModal()

  const client = useQueryClient()

  const updateProfileMutation = useMutation({
    mutationFn: updateProfileApi,
    onSuccess: () => {
      client.invalidateQueries(['user'])
    },
  })

  const [activeStep, setActiveStep] = useState<number>(0)

  useEffect(() => {
    showModal(modals.welcome)
  }, [])

  useEffect(() => {
    window.scrollTo({
      top: 0,
    })
  }, [activeStep])

  const onStepChange = (newStep: number) => {
    setActiveStep(newStep)
  }

  const updateProfile = (newAttr: ProfileAttribute[]) => {
    if (!newAttr?.length) return
    updateProfileMutation.mutate({
      attributes: newAttr,
    })
  }

  return (
    <ContentWrapper isData>
      <Typography className={classes.title}>
        {STEPS[activeStep].name}
      </Typography>
      <Grid
        container
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid container className={classes.progressContainer}>
          <FormProgressBar activeStep={activeStep} totalSteps={STEPS.length} />
        </Grid>
        {STEPS.map((step) => {
          if (step.id !== activeStep) return null
          return (
            <step.Component
              key={step.id}
              step={step}
              onStepChange={onStepChange}
              updateProfile={updateProfile}
            />
          )
        })}
      </Grid>
    </ContentWrapper>
  )
}

export default PostponedProfileForm
